import { useEffect, useRef, useState, useCallback } from 'react';

export interface UseCustomHorizontalScrollReturn {
  containerRef: React.RefObject<HTMLDivElement>;
  scrollbarRef: React.RefObject<HTMLDivElement>;
  thumbRef: React.RefObject<HTMLDivElement>;
  trackRef: React.RefObject<HTMLDivElement>;
  isDragging: boolean;
  isHovered: boolean;
  thumbWidth: number;
  thumbPosition: number;
  showScrollbar: boolean;
  handleThumbMouseDown: (e: React.MouseEvent) => void;
  handleTrackClick: (e: React.MouseEvent) => void;
  handleThumbMouseEnter: () => void;
  handleThumbMouseLeave: () => void;
}

export function useCustomHorizontalScroll(): UseCustomHorizontalScrollReturn {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollbarRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [thumbWidth, setThumbWidth] = useState(0);
  const [thumbPosition, setThumbPosition] = useState(0);
  const [showScrollbar, setShowScrollbar] = useState(false);

  const dragStartX = useRef(0);
  const dragStartScrollLeft = useRef(0);

  // Calculate thumb size and position based on scroll state
  const updateScrollbar = useCallback(() => {
    const container = containerRef.current;

    if (!container) return;

    const { scrollWidth, clientWidth, scrollLeft } = container;
    const needsScrollbar = scrollWidth > clientWidth;

    setShowScrollbar(needsScrollbar);

    if (!needsScrollbar) return;

    // Calculate thumb width as a ratio of visible area to total width
    const ratio = clientWidth / scrollWidth;
    const newThumbWidth = Math.max(clientWidth * ratio, 40); // Minimum 40px

    setThumbWidth(newThumbWidth);

    // Calculate thumb position
    const maxScrollLeft = scrollWidth - clientWidth;
    const scrollRatio = maxScrollLeft > 0 ? scrollLeft / maxScrollLeft : 0;
    const maxThumbPosition = clientWidth - newThumbWidth;
    const newThumbPosition = scrollRatio * maxThumbPosition;

    setThumbPosition(newThumbPosition);
  }, []);

  // Handle container scroll - always update to keep scrollbar in sync
  const handleScroll = useCallback(() => {
    updateScrollbar();
  }, [updateScrollbar]);

  // Handle touch events for mobile
  const handleTouchStart = useCallback((event: TouchEvent) => {
    const container = containerRef.current;

    if (!container) return;

    dragStartX.current = event.touches[0].clientX;
    dragStartScrollLeft.current = container.scrollLeft;
  }, []);

  const handleTouchMove = useCallback((event: TouchEvent) => {
    const container = containerRef.current;

    if (!container) return;

    const deltaX = dragStartX.current - event.touches[0].clientX;
    container.scrollLeft = dragStartScrollLeft.current + deltaX;
  }, []);

  // Handle thumb drag
  const handleThumbMouseDown = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    setIsDragging(true);

    dragStartX.current = event.clientX;
    dragStartScrollLeft.current = containerRef.current?.scrollLeft || 0;
  }, []);

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!isDragging) return;

      const container = containerRef.current;

      if (!container) return;

      const deltaX = event.clientX - dragStartX.current;
      const { scrollWidth, clientWidth } = container;
      const maxScrollLeft = scrollWidth - clientWidth;
      const maxThumbPosition = clientWidth - thumbWidth;

      // Calculate how much to scroll based on thumb movement
      const scrollRatio = maxThumbPosition > 0 ? deltaX / maxThumbPosition : 0;
      const newScrollLeft = dragStartScrollLeft.current + scrollRatio * maxScrollLeft;

      container.scrollLeft = Math.max(0, Math.min(newScrollLeft, maxScrollLeft));
    },
    [isDragging, thumbWidth],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Handle track click (jump to position)
  const handleTrackClick = useCallback(
    (event: React.MouseEvent) => {
      const container = containerRef.current;
      const track = trackRef.current;

      if (!container || !track) return;

      // Don't handle if clicking on the thumb itself
      if (event.target === thumbRef.current) return;

      const trackRect = track.getBoundingClientRect();
      const clickX = event.clientX - trackRect.left;
      const { scrollWidth, clientWidth } = container;
      const maxScrollLeft = scrollWidth - clientWidth;

      // Calculate where to scroll based on click position and center the thumb on the click position
      const thumbCenter = clickX - thumbWidth / 2;
      const maxThumbPosition = clientWidth - thumbWidth;
      const scrollRatio = maxThumbPosition > 0 ? thumbCenter / maxThumbPosition : 0;
      const newScrollLeft = scrollRatio * maxScrollLeft;

      container.scrollLeft = Math.max(0, Math.min(newScrollLeft, maxScrollLeft));
    },
    [thumbWidth],
  );

  const handleThumbMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleThumbMouseLeave = useCallback(() => {
    if (!isDragging) {
      setIsHovered(false);
    }
  }, [isDragging]);

  // Set up event listeners
  useEffect(() => {
    const container = containerRef.current;

    if (!container) return;

    // Initial calculation
    updateScrollbar();

    // Scroll listener
    container.addEventListener('scroll', handleScroll, { passive: true });

    // Touch listeners
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: true });

    // Resize observer to handle container size changes
    const resizeObserver = new ResizeObserver(() => {
      updateScrollbar();
    });
    resizeObserver.observe(container);

    // Mutation observer to handle content changes
    const mutationObserver = new MutationObserver(() => {
      updateScrollbar();
    });

    mutationObserver.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });

    return () => {
      container.removeEventListener('scroll', handleScroll);
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [handleScroll, handleTouchStart, handleTouchMove, updateScrollbar]);

  // Handle mouse move and up during drag
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      // Prevent text selection during drag
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'grabbing';

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Clear hover state when drag ends
  useEffect(() => {
    if (!isDragging) {
      setIsHovered(false);
    }
  }, [isDragging]);

  return {
    containerRef,
    scrollbarRef,
    thumbRef,
    trackRef,
    isDragging,
    isHovered,
    thumbWidth,
    thumbPosition,
    showScrollbar,
    handleThumbMouseDown,
    handleTrackClick,
    handleThumbMouseEnter,
    handleThumbMouseLeave,
  };
}
